import axios from 'axios'

function normalizeApiBaseUrl(rawBaseUrl) {
  // Local dev: CRA proxy uses "/api" relative path.
  if (!rawBaseUrl) return '/api'

  // Remove trailing slashes so we can safely append.
  const trimmed = rawBaseUrl.replace(/\/+$/, '')

  // Backend routes are under "/api/..." (see Django `survey_management/urls.py`).
  if (trimmed.endsWith('/api')) return trimmed
  return `${trimmed}/api`
}

// IMPORTANT (Local Development):
// For local development, we use the relative /api path which is proxied by package.json
// This ensures cookies work correctly (first-party cookies).
// 
// - Local dev: CRA proxy (package.json "proxy") makes `/api` work.
// - Production: Use relative `/api` path - server.js proxies to backend.
// 
const getApiBaseUrl = () => {
  if (typeof window === 'undefined') {
    // Server-side rendering
    return normalizeApiBaseUrl(process.env.REACT_APP_API_URL)
  }
  
  // Use relative path for all environments
  console.log('[API] Using relative path /api')
  return '/api'
}

const API_BASE_URL = getApiBaseUrl()

// Create axios instance with default config
// Use relative URL with proxy for all environments
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Important for Django session cookies
})

// Request interceptor to add CSRF token and session
api.interceptors.request.use(
  (config) => {
    // Get CSRF token and session ID from stored values
    let csrfToken = getCookie('csrftoken') || storedCsrfToken
    let sessionId = getCookie('sessionid') || storedSessionId
    
    // For state-changing methods, ensure we have a CSRF token
    const isStateChanging = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(config.method?.toUpperCase())
    
    if (isStateChanging && !config.url?.includes('/auth/csrf-token/')) {
      if (!csrfToken) {
        console.warn('[API] CSRF token missing for state-changing request:', config.url)
        console.warn('[API] Available cookies:', document.cookie)
        console.warn('[API] Stored CSRF token:', storedCsrfToken)
        console.warn('[API] Request may fail. Token should be fetched from /auth/csrf-token/ first.')
        // Try to fetch CSRF token automatically if missing
        if (typeof window !== 'undefined') {
          const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
          
          if (isLocal) {
            console.log('[API] Attempting to fetch CSRF token automatically...')
            // Don't await here, just trigger the fetch
            api.get('/auth/csrf-token/').then(response => {
              console.log('[API] CSRF token fetched successfully')
            }).catch(err => {
              console.error('[API] Failed to fetch CSRF token:', err)
            })
          }
        }
      } else {
        config.headers['X-CSRFToken'] = csrfToken
        console.log('[API] CSRF token added to request:', csrfToken.substring(0, 10) + '...')
      }
      
      // Add session ID to header for cross-domain requests only
      if (sessionId && typeof window !== 'undefined') {
        const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
        
        // Only manually set cookies for truly cross-domain requests
        if (!isLocal) {
          config.headers['Cookie'] = `sessionid=${sessionId}`
        }
      }
    } else if (csrfToken) {
      // For GET requests, include token if available but don't require it
      config.headers['X-CSRFToken'] = csrfToken
      
      // Add session ID to header for cross-domain requests only
      if (sessionId && typeof window !== 'undefined') {
        const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
        
        // Only manually set cookies for truly cross-domain requests
        if (!isLocal) {
          config.headers['Cookie'] = `sessionid=${sessionId}`
        }
      }
    }
    
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    // Store CSRF token from response if present (for cross-domain cookie issues)
    if (response.data?.csrfToken) {
      storedCsrfToken = response.data.csrfToken
      console.log('[API] Stored CSRF token from response:', storedCsrfToken.substring(0, 10) + '...')
    }
    
    // Also try to extract CSRF token from Set-Cookie header if present
    const setCookieHeader = response.headers['set-cookie'] || response.headers['Set-Cookie']
    if (setCookieHeader) {
      const cookies = Array.isArray(setCookieHeader) ? setCookieHeader : [setCookieHeader]
      for (const cookie of cookies) {
        const csrfMatch = cookie.match(/csrftoken=([^;]+)/)
        if (csrfMatch && csrfMatch[1]) {
          storedCsrfToken = decodeURIComponent(csrfMatch[1])
          console.log('[API] Extracted CSRF token from Set-Cookie header')
        }
        const sessionMatch = cookie.match(/sessionid=([^;]+)/)
        if (sessionMatch && sessionMatch[1]) {
          storedSessionId = decodeURIComponent(sessionMatch[1])
          console.log('[API] Extracted sessionid from Set-Cookie header')
        }
      }
    }
    
    // Log response for auth endpoints
    if (response.config?.url?.includes('/auth/')) {
      console.log('[API] Response from:', response.config.url, 'Status:', response.status)
      // Check if cookies were set in response headers
      const setCookieHeader = response.headers['set-cookie'] || response.headers['Set-Cookie']
      if (setCookieHeader) {
        console.log('[API] Cookies set in response:', Array.isArray(setCookieHeader) ? setCookieHeader : [setCookieHeader])
      } else {
        console.warn('[API] No Set-Cookie header in response!')
        console.log('[API] Response headers:', Object.keys(response.headers))
      }
    }
    
    // Handle 204 No Content responses (common for DELETE requests)
    if (response.status === 204) {
      // Return a success response object even for 204
      return { ...response, data: { status: 'success', message: 'Deleted successfully' } }
    }
    return response
  },
  (error) => {
    // Log error details for debugging
    if (error.response?.status === 403 || error.response?.status === 401) {
      console.error('[API] Auth error:', {
        url: error.config?.url,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        headers: error.response?.headers
      })
    }
    
    // Handle CSRF errors specifically - try to get a fresh token and retry
    // Check for CSRF errors in multiple possible formats
    const errorDetail = error.response?.data?.detail || error.response?.data?.message || ''
    const isCsrfError = error.response?.status === 403 && (
      errorDetail.includes('CSRF') || 
      errorDetail.includes('csrf') ||
      errorDetail.includes('Forbidden') ||
      (errorDetail.includes('token') && errorDetail.includes('incorrect'))
    )
    
    if (isCsrfError) {
      console.warn('[API] CSRF error detected:', errorDetail)
      console.warn('[API] Fetching new CSRF token and retrying...')
      
      // Try to get a fresh CSRF token
      return api.get('/auth/csrf-token/')
        .then(csrfResponse => {
          if (csrfResponse.data?.csrfToken) {
            storedCsrfToken = csrfResponse.data.csrfToken
            console.log('[API] Got fresh CSRF token, retrying original request...')
            // Retry the original request with new token
            const originalConfig = { ...error.config }
            originalConfig.headers = { ...originalConfig.headers }
            originalConfig.headers['X-CSRFToken'] = storedCsrfToken
            // Ensure withCredentials is set
            originalConfig.withCredentials = true
            return api(originalConfig)
          }
          console.error('[API] Could not get CSRF token from response')
          return Promise.reject(error)
        })
        .catch((retryError) => {
          console.error('[API] Failed to retry request after CSRF error:', retryError)
          return Promise.reject(error)
        })
    }
    
    // Don't redirect during login flow or auth checks - let the auth handler manage it
    const isAuthFlow = error.config?.url?.includes('/auth/login') || 
                       error.config?.url?.includes('/auth/user') ||
                       error.config?.url?.includes('/auth/csrf-token')
    
    // Check if we have a user stored (don't redirect if we do)
    const storedUser = localStorage.getItem('user')
    const hasStoredUser = storedUser !== null
    const loginTimestamp = localStorage.getItem('loginTimestamp')
    const recentLogin = loginTimestamp && (Date.now() - parseInt(loginTimestamp) < 300000) // 5 minutes
    
    // Only redirect on 401/403 if:
    // 1. It's not an auth flow
    // 2. It's not a CSRF error (we handle that above)
    // 3. We're not already on login page
    // 4. Recent login is false (if recent, cookies might still be propagating)
    if ((error.response?.status === 401 || error.response?.status === 403) && !isAuthFlow && !recentLogin && !isCsrfError) {
      // Check if we're in a protected route before redirecting
      const protectedRoutes = ['/', '/home', '/dashboard', '/surveys', '/operations', '/admin', '/forms']
      const isProtectedRoute = protectedRoutes.some(route => window.location.pathname.startsWith(route))
      
      // If we have a stored user but are getting 403, clear it (stale data)
      if (hasStoredUser && error.response?.status === 403) {
        console.warn('Clearing stale user data - getting 403 despite stored user')
        localStorage.removeItem('user')
        localStorage.removeItem('loginTimestamp')
      }
      
      // Only redirect if we're on a protected route and not already on login
      if (isProtectedRoute && window.location.pathname !== '/login') {
        // Small delay to avoid race conditions
        setTimeout(() => {
          if (window.location.pathname !== '/login') {
            console.log('[API] Redirecting to login due to 403/401 error')
            window.location.href = '/login'
          }
        }, 100)
      }
    } else if (recentLogin && (error.response?.status === 401 || error.response?.status === 403)) {
      // If we just logged in and get 401/403, cookies might still be propagating
      console.warn('API call failed but keeping user state (cookies may still be propagating):', error.config?.url)
    }
    return Promise.reject(error)
  }
)

// Store CSRF token from response body (since cross-domain cookies aren't readable)
let storedCsrfToken = null
let storedSessionId = null

// Helper function to get cookie value
function getCookie(name) {
  // First try to get from stored token (from response body)
  if (name === 'csrftoken' && storedCsrfToken) {
    return storedCsrfToken
  }
  if (name === 'sessionid' && storedSessionId) {
    return storedSessionId
  }
  
  // For same-origin requests (local dev, production proxy), try document.cookie
  if (typeof window !== 'undefined') {
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    
    if (isLocal) {
      // Same-origin requests can access cookies directly
      const value = `; ${document.cookie}`
      const parts = value.split(`; ${name}=`)
      if (parts.length === 2) {
        return parts.pop().split(';').shift()
      }
    }
  }
  
  // For cross-domain cookies, we need to get them from axios interceptors
  // document.cookie won't work for cross-domain cookies due to browser security
  return null // We'll handle this differently for cross-domain
}

// Auth API
export const authAPI = {
  getCsrfToken: () => api.get('/auth/csrf-token/'),
  login: (username, password) => api.post('/auth/login/', { username, password }),
  logout: () => api.post('/auth/logout/'),
  getCurrentUser: () => api.get('/auth/user/'),
  changePassword: (data) => api.post('/auth/change-password/', data),
}

// Survey API
export const surveyAPI = {
  list: (params) => api.get('/surveys/', { params }),
  get: (id) => api.get(`/surveys/${id}/`),
  create: (data) => api.post('/surveys/', data),
  update: (id, data) => api.put(`/surveys/${id}/`, data),
  delete: (id) => api.delete(`/surveys/${id}/`),
  toggleStatus: (id) => api.post(`/surveys/${id}/toggle-status/`),
  toggleInvoiceSent: (id, invoiceSent) => api.post(`/surveys/${id}/toggle-invoice-sent/`, { invoice_sent: invoiceSent }),
  export: (params) => api.get('/surveys/export/', { params, responseType: 'blob' }),
  generateReference: () => api.get('/surveys/generate-reference/'),
  getInvoiceData: (id) => api.get(`/surveys/${id}/invoice-data/`),
  saveInvoice: (id, data) => api.patch(`/surveys/${id}/invoice-save/`, data),
}

// Survey Reports API - files attached to one specific survey (by reference number).
// Access mirrors survey access; download always goes through the authenticated
// download endpoint (each report's download_url), never a raw storage URL.
export const reportsAPI = {
  list: (referenceNumber) => api.get(`/surveys/${referenceNumber}/reports/`),
  upload: (referenceNumber, formData) => api.post(`/surveys/${referenceNumber}/reports/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  delete: (referenceNumber, reportId) => api.delete(`/surveys/${referenceNumber}/reports/${reportId}/`),
}

// Operation API
export const operationAPI = {
  list: () => api.get('/operations/'),
  get: (id) => api.get(`/operations/${id}/`),
  create: (data) => api.post('/operations/', data),
  update: (id, data) => api.put(`/operations/${id}/`, data),
  delete: (id) => api.delete(`/operations/${id}/`),
  createFromSurvey: (surveyRef) => api.post('/operations/create-from-survey/', { survey_ref: surveyRef }),
  deleteByRef: (referenceNumber) => api.post(`/operations/${referenceNumber}/delete/`),
  getDashboardData: (params) => api.get('/operations/dashboard-data/', { params }),
  save: (data) => api.post('/operations/save/', data),
}

// Dashboard API
export const dashboardAPI = {
  getStats: (params) => api.get('/dashboard/stats/', { params }),
  generateSOA: (params) => api.get('/dashboard/generate-soa/', { params }),
  exportSOA: (params) => api.get('/dashboard/export-soa/', { params, responseType: 'blob' }),
}

// Reference Data API
export const referenceAPI = {
  getPorts: (country) => api.get('/ports/', { params: { country } }),
  getSurveyTypes: () => api.get('/survey-types/'),
  getSurveyorsByType: (type) => {
    const params = type ? { type } : {}
    return api.get('/surveyors/by-type/', { params })
  },
  getCountries: () => api.get('/countries/'),
  getClients: () => api.get('/clients/'),
}

// Admin API
export const adminAPI = {
  // Groups
  getGroups: () => api.get('/admin/groups/'),
  // Users
  getUsers: () => api.get('/admin/users/'),
  createUser: (data) => api.post('/admin/users/', data),
  updateUser: (id, data) => api.put(`/admin/users/${id}/`, data),
  deleteUser: (id) => api.delete(`/admin/users/${id}/`),
  // Surveyors
  getSurveyors: () => api.get('/admin/surveyors/'),
  createSurveyor: (data) => api.post('/admin/surveyors/', data),
  updateSurveyor: (id, data) => api.put(`/admin/surveyors/${id}/`, data),
  deleteSurveyor: (id) => api.delete(`/admin/surveyors/${id}/`),
  // Surveyor Groups
  getSurveyorGroups: () => api.get('/admin/surveyor-groups/'),
  createSurveyorGroup: (data) => api.post('/admin/surveyor-groups/', data),
  updateSurveyorGroup: (id, data) => api.put(`/admin/surveyor-groups/${id}/`, data),
  deleteSurveyorGroup: (id) => api.delete(`/admin/surveyor-groups/${id}/`),
  // Surveyor Group Members
  getSurveyorGroupMembers: () => api.get('/admin/surveyor-group-members/'),
  createSurveyorGroupMember: (data) => api.post('/admin/surveyor-group-members/', data),
  updateSurveyorGroupMember: (id, data) => api.put(`/admin/surveyor-group-members/${id}/`, data),
  deleteSurveyorGroupMember: (id) => api.delete(`/admin/surveyor-group-members/${id}/`),
  // Clients
  getClients: () => api.get('/admin/clients/'),
  createClient: (data) => api.post('/admin/clients/', data),
  updateClient: (code, data) => api.put(`/admin/clients/${code}/`, data),
  deleteClient: (code) => api.delete(`/admin/clients/${code}/`),
  // Countries
  getCountries: () => api.get('/admin/countries/'),
  createCountry: (data) => api.post('/admin/countries/', data),
  updateCountry: (id, data) => api.put(`/admin/countries/${id}/`, data),
  deleteCountry: (id) => api.delete(`/admin/countries/${id}/`),
  // Ports
  getPorts: () => api.get('/admin/ports/'),
  createPort: (data) => api.post('/admin/ports/', data),
  updatePort: (id, data) => api.put(`/admin/ports/${id}/`, data),
  deletePort: (id) => api.delete(`/admin/ports/${id}/`),
  // Survey Types
  getSurveyTypes: () => api.get('/admin/survey-types/'),
  createSurveyType: (data) => api.post('/admin/survey-types/', data),
  updateSurveyType: (id, data) => api.put(`/admin/survey-types/${id}/`, data),
  deleteSurveyType: (id) => api.delete(`/admin/survey-types/${id}/`),
  // Operations
  getOperations: () => api.get('/admin/operations/'),
  createOperation: (data) => api.post('/admin/operations/', data),
  updateOperation: (id, data) => api.put(`/admin/operations/${id}/`, data),
  deleteOperation: (id) => api.delete(`/admin/operations/${id}/`),
  // Bank Accounts
  getBankAccounts: () => api.get('/admin/bank-accounts/'),
  createBankAccount: (data) => api.post('/admin/bank-accounts/', data),
  updateBankAccount: (id, data) => api.put(`/admin/bank-accounts/${id}/`, data),
  deleteBankAccount: (id) => api.delete(`/admin/bank-accounts/${id}/`),
  // Company Info
  getCompanyInfo: () => api.get('/admin/company-info/'),
  createCompanyInfo: (data) => {
    // If data is FormData, don't set Content-Type header (let browser set it with boundary)
    if (data instanceof FormData) {
      return api.post('/admin/company-info/', data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
    }
    return api.post('/admin/company-info/', data)
  },
  updateCompanyInfo: (id, data) => {
    // If data is FormData, don't set Content-Type header (let browser set it with boundary)
    if (data instanceof FormData) {
      return api.put(`/admin/company-info/${id}/`, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
    }
    return api.put(`/admin/company-info/${id}/`, data)
  },
  deleteCompanyInfo: (id) => api.delete(`/admin/company-info/${id}/`),
  // Bank Info
  getBankInfo: () => api.get('/admin/bank-info/'),
  createBankInfo: (data) => api.post('/admin/bank-info/', data),
  updateBankInfo: (id, data) => api.put(`/admin/bank-info/${id}/`, data),
  deleteBankInfo: (id) => api.delete(`/admin/bank-info/${id}/`),
  // AI API Configs
  getAIApiConfigs: () => api.get('/admin/ai-api-configs/'),
  createAIApiConfig: (data) => api.post('/admin/ai-api-configs/', data),
  updateAIApiConfig: (id, data) => api.put(`/admin/ai-api-configs/${id}/`, data),
  deleteAIApiConfig: (id) => api.delete(`/admin/ai-api-configs/${id}/`),
}

export default api

